import { MagicBackground, PersistentStore } from "@/stores/persistent_store";
import { computed, inject, reactive, Ref, ref, watch } from "vue";
import { useEditorStore, ViewMode } from "@/stores/editor_store";
import Client from "@/modules/internal_api/client";
import { ProcessingState, Image, ResultVariant, ProcessingError } from "@/modules/internal_api/image";
import { usePoll } from "./poll";
import { rbgMbGenerationClickedV100, rbgEditorNavigationClickedV100 } from "kaleido-event-tracker";

import { I18n } from "i18n-js";
import User from "@/modules/user";
import { useCredits } from "./credits";
import { useMagicBackgroundFlag } from "./magic_background_flag";

export enum WidgetState {
  LoggedOut = "logged_out",
  Ready = "ready",
  Loading = "loading",
  Error = "error",
  Generated = "generated",
}

const activeGenerations = reactive(
  new Map<
    string,
    {
      persistentStoreId: string;
      templateId: string;
      iterationId?: string;
      startTime: number;
      persistentStoreRef: PersistentStore;
    }
  >()
);

const ACTIVE_GENERATION_TIMEOUT = 2 * 60 * 1000;

export function useMagicBackground(
  persistentStore: Ref<PersistentStore>,
  getPersistentStore?: (image: Image) => PersistentStore | null
) {
  type WidgetIcon = "generate" | "generating" | "generated";
  interface WidgetProps {
    variant: "primary" | "overlay";
    copy: string;
    badgeAvailable?: boolean;
    icon?: WidgetIcon;
    action?: () => Promise<void> | void;
  }

  const I18n: I18n = inject("I18n");

  const { poll } = usePoll();
  const { credits, isLoading: isLoadingCredits, fetchCredits, GENERATE_BACKGROUND_CREDIT_COST } = useCredits();
  const error = ref<ProcessingError | Error | null>(null);

  const handleProcessingError = (processingError: ProcessingError) => {
    error.value = processingError;
  };

  const parseBackgrounds = (image: Image): MagicBackground[] => {
    const backgrounds = image.magicBackgroundResults;
    const parsedBackgrounds = [];

    backgrounds?.forEach((result, id) => {
      if ("url" in result.magicBackgroundPreviewResult) {
        parsedBackgrounds.push({
          id,
          state: result.magicBackgroundPreviewResult.state,
          url: result.magicBackgroundPreviewResult.url,
          templateId: result.magicBackgroundPreviewResult.templateId,
          iterationId: result.magicBackgroundPreviewResult.iterationId,
        });
      }
    });

    return parsedBackgrounds;
  };

  const getActiveGenerationPersistentStore = (imageId: string): PersistentStore | null => {
    const activeGeneration = activeGenerations.get(imageId);
    if (!activeGeneration) return null;

    if (getPersistentStore) {
      const image = editorStore.images.find((img) => img.meta.id === imageId);
      if (image) {
        const targetPersistentStore = getPersistentStore(image);
        if (targetPersistentStore && targetPersistentStore.imageId === activeGeneration.persistentStoreId) {
          return targetPersistentStore;
        }
      }
    }

    if (activeGeneration.persistentStoreId === persistentStore.value.imageId) {
      return persistentStore.value;
    }

    console.warn(
      `Cannot find persistent store for image ${imageId} - generation was initiated by ${activeGeneration.persistentStoreId} but current store is ${persistentStore.value.imageId}`
    );
    return null;
  };

  const clearLoadingState = (imageId: string) => {
    const targetPersistentStore = getActiveGenerationPersistentStore(imageId);
    if (targetPersistentStore) {
      targetPersistentStore.setGeneratingBackground(false);
    } else {
      const activeGeneration = activeGenerations.get(imageId);
      if (activeGeneration && activeGeneration.persistentStoreId === persistentStore.value.imageId) {
        persistentStore.value.setGeneratingBackground(false);
      }
    }
  };

  const isLoading = computed(() => {
    if (!editorStore.selectedImage?.magicBackgroundResults) {
      return false;
    }

    const currentImageId = editorStore.selectedImage?.meta?.id;
    const hasActiveGeneration = activeGenerations.has(currentImageId);

    const isProcessingResult = Array.from(editorStore.selectedImage.magicBackgroundResults.values()).some(
      (result) => result.magicBackgroundPreviewResult.state === ProcessingState.Processing
    );

    return hasActiveGeneration || isProcessingResult;
  });

  const hasActiveGenerationForImage = (imageId: string): boolean => {
    return activeGenerations.has(imageId);
  };

  const generatedBackgrounds = computed(() => {
    const currentImage = editorStore.selectedImage;
    if (!currentImage) {
      return [];
    }
    return parseBackgrounds(currentImage);
  });

  const selectedTemplate = computed(() => {
    return persistentStore.value.selectedMagicBackgroundTemplate;
  });

  const hasError = computed(() => !!error.value);
  const selectedMagicBackground = computed(() => {
    return persistentStore.value.selectedMagicBackground;
  });

  const hasSelectedMagicBackground = computed(() => {
    return persistentStore.value.selectedMagicBackground !== null;
  });

  const hasSelectedMagicBackgroundTemplate = computed(() => {
    return selectedTemplate.value !== null;
  });

  const isGenerated = computed(
    () =>
      hasSelectedMagicBackground.value &&
      persistentStore.value.selectedMagicBackground.state === ProcessingState.Finished
  );

  const currentState = computed(() => {
    if (!User.signedIn() || credits.value < GENERATE_BACKGROUND_CREDIT_COST) return WidgetState.LoggedOut;
    if (error.value) return WidgetState.Error;
    if (isLoading.value || isLoadingCredits.value || persistentStore.value.generatingBackground)
      return WidgetState.Loading;
    if (isGenerated.value) return WidgetState.Generated;
    if (persistentStore.value.selectedMagicBackground === null && selectedTemplate.value !== null)
      return WidgetState.Ready;
    if (persistentStore.value.selectedMagicBackground !== null && editorStore.openedPanel !== "addBackground")
      return WidgetState.Generated;
    return WidgetState.Ready;
  });

  const widgetBehaviorMap: Record<WidgetState, WidgetProps> = {
    [WidgetState.LoggedOut]: {
      variant: "primary",
      copy: I18n.t("editor.magic_background.variants.variant_a.generate"),
      icon: "generate",
      badgeAvailable: true,
      action: () => {
        const path = User.signedIn() ? "pricing" : "users/sign_up";
        document.location.href = `${path}?utm_source=generate-scene`;
      },
    },
    [WidgetState.Ready]: {
      variant: "primary",
      copy: I18n.t("editor.magic_background.variants.variant_a.generate"),
      icon: "generate",
      badgeAvailable: true,
      action: async () => await generateMagicBackground(selectedTemplate.value.id),
    },
    [WidgetState.Loading]: {
      variant: "primary",
      copy: "",
      icon: "generating",
      badgeAvailable: false,
    },
    [WidgetState.Error]: {
      variant: "overlay",
      copy: I18n.t("editor.magic_background.generate_generic_error"),
      badgeAvailable: true,
    },
    [WidgetState.Generated]: {
      variant: "primary",
      copy: I18n.t("editor.magic_background.variants.variant_a.generate_again"),
      icon: "generate",
      badgeAvailable: true,
      action: async () => await regenerateMagicBackground(),
    },
  };

  const currentWidgetConfig = computed((): WidgetProps => {
    return widgetBehaviorMap[currentState.value];
  });

  const editorStore = useEditorStore();

  watch(
    () => editorStore.selectedImage?.meta?.id,
    (newImageId, oldImageId) => {
      if (oldImageId && oldImageId !== newImageId) {
        error.value = null;

        const oldActiveGeneration = activeGenerations.get(oldImageId);
        if (oldActiveGeneration && getPersistentStore) {
          const oldImage = editorStore.images.find((img) => img.meta.id === oldImageId);
          if (oldImage) {
            const oldPersistentStore = getPersistentStore(oldImage);
            if (oldPersistentStore && oldPersistentStore.imageId === oldActiveGeneration.persistentStoreId) {
              oldPersistentStore.setGeneratingBackground(false);
            }
          }
        }
      }
    }
  );

  watch(
    () => editorStore.images.map((img) => img.meta.id),
    (newImageIds, oldImageIds) => {
      if (oldImageIds) {
        const deletedImageIds = oldImageIds.filter((id) => !newImageIds.includes(id));

        deletedImageIds.forEach((imageId) => {
          if (activeGenerations.has(imageId)) {
            const activeGeneration = activeGenerations.get(imageId);

            if (getPersistentStore) {
              const oldImage = editorStore.images.find((img) => img.meta.id === imageId);
              if (oldImage) {
                const oldPersistentStore = getPersistentStore(oldImage);
                if (oldPersistentStore && oldPersistentStore.imageId === activeGeneration.persistentStoreId) {
                  oldPersistentStore.setGeneratingBackground(false);
                }
              }
            }

            activeGenerations.delete(imageId);
          }
        });
      }
    }
  );

  watch(
    () => editorStore.openedPanel,
    () => {
      if (
        (editorStore.openedPanel === "fx" || editorStore.openedPanel === "eraseRestore") &&
        persistentStore.value.selectedMagicBackground !== null
      ) {
        persistentStore.value.selectMagicBackground(null);
      }
    }
  );

  const enqueueMagicBackground = async (templateId: string, iterationId?: string) => {
    const image = editorStore.selectedImage;
    const imageId = image.meta.id;
    const persistentStoreId = persistentStore.value.imageId;

    activeGenerations.set(imageId, {
      persistentStoreId,
      templateId,
      iterationId,
      startTime: Date.now(),
      persistentStoreRef: persistentStore.value,
    });

    persistentStore.value.setGeneratingBackground(true);

    const timeoutId = setTimeout(() => {
      const activeGeneration = activeGenerations.get(imageId);
      if (activeGeneration) {
        clearLoadingState(imageId);
        activeGenerations.delete(imageId);
      }
    }, ACTIVE_GENERATION_TIMEOUT);

    try {
      let updatedImage = await Client.enqueueMagicBackground(image, templateId, iterationId);
      updatedImage = await poll(updatedImage, ResultVariant.MagicBackgroundPreview, handleProcessingError);

      editorStore.updateImage(updatedImage);

      if (updatedImage.magicBackgroundResults) {
        const generation = Array.from(updatedImage.magicBackgroundResults.entries()).pop()?.[1];

        if (generation.magicBackgroundPreviewResult.state === ProcessingState.Finished) {
          const parsedBackgrounds = parseBackgrounds(updatedImage);
          if (parsedBackgrounds.length > 0) {
            // Find the newest finished background
            const finishedBackgrounds = parsedBackgrounds.filter((bg) => bg.state === ProcessingState.Finished);
            const newestBackground = finishedBackgrounds.pop(); // Get the last (newest) finished background

            if (newestBackground) {
              // Always update the persistent store for the specific image where generation completed
              if (getPersistentStore) {
                // Find the image that had the generation
                const targetImage = editorStore.images.find((img) => img.meta.id === imageId);
                if (targetImage) {
                  const targetPersistentStore = getPersistentStore(targetImage);
                  if (targetPersistentStore) {
                    // Always update with newest background, regardless of current selection
                    targetPersistentStore.setSelectedMagicBackgroundTemplate(null);
                    targetPersistentStore.selectMagicBackground(newestBackground);
                  }
                }
              } else {
                // Fallback: use the stored persistent store reference from when generation started
                const activeGeneration = activeGenerations.get(imageId);
                if (activeGeneration?.persistentStoreRef) {
                  const storedPersistentStore = activeGeneration.persistentStoreRef;
                  storedPersistentStore.setSelectedMagicBackgroundTemplate(null);
                  storedPersistentStore.selectMagicBackground(newestBackground);
                }
              }
            }
          }
        } else if (generation.magicBackgroundPreviewResult.state === ProcessingState.Error) {
          error.value = generation.magicBackgroundPreviewResult as ProcessingError;
        }

        clearLoadingState(imageId);
        activeGenerations.delete(imageId);
        fetchCredits();
      }
    } catch (error) {
      handleProcessingError({
        message: `${error.message}`,
        code: "GENERATION_ERROR",
        state: ProcessingState.Error,
      });

      clearLoadingState(imageId);
    } finally {
      clearTimeout(timeoutId);
      clearLoadingState(imageId);

      activeGenerations.delete(imageId);
    }
  };

  const generateMagicBackground = async (templateId: string) => {
    const cache = persistentStore.value.magicBrushCache;
    const iterationId = cache?.iterationId;

    rbgMbGenerationClickedV100({ image_id: editorStore.selectedImage.meta.id, template_id: templateId });
    await enqueueMagicBackground(templateId, iterationId);
  };

  const regenerateMagicBackground = async () => {
    const templateId = persistentStore.value.selectedMagicBackground?.templateId;
    const iterationId = persistentStore.value.selectedMagicBackground?.iterationId;

    rbgMbGenerationClickedV100({ image_id: editorStore.selectedImage.meta.id, template_id: templateId });
    await enqueueMagicBackground(templateId, iterationId);
  };

  const resetError = () => {
    if (error.value) {
      error.value = null;
    }
  };

  const isGenerateMode = computed(() => {
    const shouldShowGenerateMode =
      (hasSelectedMagicBackgroundTemplate.value || persistentStore.value.selectedMagicBackground !== null) &&
      editorStore.openedPanel !== "eraseRestore" &&
      editorStore.openedPanel !== "fx";
    return shouldShowGenerateMode;
  });

  const shouldShowIntroWidget = computed(() => {
    return (
      editorStore.selectedImage?.previewResult.state === ProcessingState.Finished &&
      persistentStore.value.selectedMagicBackground === null &&
      persistentStore.value.selectedMagicBackgroundTemplate === null &&
      editorStore.openedPanel === null
    );
  });

  const shouldShowSelectInspirationWidget = computed(() => {
    return (
      editorStore.openedPanel === "addBackground" &&
      persistentStore.value.shouldShowSelectInspirationWidget &&
      selectedTemplate.value === null
    );
  });

  const hideSelectInspirationWidget = () => {
    persistentStore.value.withSnapshot(() => {
      persistentStore.value.disableSelectInspirationWidget();
    });
  };

  const generatedBackgroundCount = computed(() => {
    return generatedBackgrounds.value.length;
  });

  // ===== MB controls =====
  const isNavigationEnabled = computed(() => {
    return (
      (generatedBackgroundCount.value > 0 || persistentStore.value.generatingBackground) &&
      (editorStore.openedPanel === "addBackground" || editorStore.openedPanel === null) &&
      editorStore.viewMode !== ViewMode.Before &&
      !persistentStore.value.canZoomOut
    );
  });

  const hasNoMagicBackgroundsSelected = computed(() => {
    return persistentStore.value.selectedMagicBackground === null && generatedBackgroundCount.value > 0;
  });

  const isFirstBackground = computed(() => {
    return (
      generatedBackgrounds.value.findIndex(
        (background) => background.id === persistentStore.value.selectedMagicBackground?.id
      ) === 0
    );
  });

  const isLastBackground = computed(() => {
    return (
      generatedBackgrounds.value.findIndex(
        (background) => background.id === persistentStore.value.selectedMagicBackground?.id
      ) ===
      generatedBackgroundCount.value - 1
    );
  });

  const nextBackground = () => {
    const currentIndex = generatedBackgrounds.value.findIndex(
      (background) => background.id === persistentStore.value.selectedMagicBackground?.id
    );

    if (currentIndex === generatedBackgroundCount.value - 1) return;
    if (hasNoMagicBackgroundsSelected.value) {
      trackNavigation("generation");
      persistentStore.value.withSnapshot(() => {
        const firstBackground = generatedBackgrounds.value[0];
        persistentStore.value.selectMagicBackground(firstBackground);
      });
      return;
    }
    const nextIndex = (currentIndex + 1) % generatedBackgroundCount.value;

    trackNavigation("generation");
    persistentStore.value.withSnapshot(() => {
      persistentStore.value.selectMagicBackground(generatedBackgrounds.value[nextIndex]);
    });
  };

  const previousBackground = () => {
    const currentIndex = generatedBackgrounds.value.findIndex(
      (background) => background.id === persistentStore.value.selectedMagicBackground?.id
    );
    if (currentIndex === -1) return;

    if (isFirstBackground.value) {
      persistentStore.value.withSnapshot(() => {
        persistentStore.value.selectMagicBackground(null);
      });
      trackNavigation("original");
      return;
    }
    const previousIndex = (currentIndex - 1 + generatedBackgroundCount.value) % generatedBackgroundCount.value;

    trackNavigation("generation");
    persistentStore.value.withSnapshot(() => {
      persistentStore.value.selectMagicBackground(generatedBackgrounds.value[previousIndex]);
    });
  };

  const trackNavigation = (selectedImageType: "generation" | "inspiration" | "original") => {
    rbgEditorNavigationClickedV100({
      image_id: editorStore.selectedImage.meta.id,
      image_type: selectedImageType,
    });
  };

  const clearStuckLoadingStates = () => {
    if (getPersistentStore) {
      editorStore.images.forEach((image) => {
        const persistentStore = getPersistentStore(image);
        if (persistentStore && persistentStore.generatingBackground) {
          persistentStore.setGeneratingBackground(false);
        }
      });
    }
  };

  // Clear stuck states on initialization
  clearStuckLoadingStates();

  return {
    generatedBackgrounds,
    // States
    isLoading,
    hasError,
    isGenerated,
    isGenerateMode,
    error,
    currentWidgetConfig,
    currentState,
    shouldShowIntroWidget,
    shouldShowSelectInspirationWidget,
    selectedMagicBackground,
    selectedTemplate,
    // Actions
    enqueueMagicBackground,
    regenerateMagicBackground,
    resetError,
    parseBackgrounds,
    nextBackground,
    previousBackground,
    hideSelectInspirationWidget,
    // Navigation controls
    isNavigationEnabled,
    isFirstBackground,
    isLastBackground,
    hasNoMagicBackgroundsSelected,
    // Cleanup
    cleanupActiveGenerations: () => {
      // Clear all loading states before clearing the map
      activeGenerations.forEach((_, imageId) => {
        const targetPersistentStore = getActiveGenerationPersistentStore(imageId);
        if (targetPersistentStore) {
          targetPersistentStore.setGeneratingBackground(false);
        }
      });
      activeGenerations.clear();
    },
    hasActiveGenerationForImage,
    clearStuckLoadingStates,

    // Credits
    fetchCredits,
    isLoadingCredits,
  };
}
